import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { alert } from '../Alert/Alert'
import { mvmResultMutedStyle, mvmResultSummaryTextStyle, resourceIconForKey } from './mvmBattleResultUi'
import { MVM_RESULT_MODAL_RV, MvMResultModalShell, MVM_RESULT_MODAL_OVERLAY_Z } from './mvmResultModalLayout'
import { MVM_BATTLE_UI_FONT_FAMILY } from '../../pages/hex-battle/constants'

const TEXT_PRIMARY = 'rgba(228,220,200,.95)'
const BORDER = 'rgb(56,44,34)'

export type MvMDefeatModalMode = 'castle_owner' | 'standard'

/** Socket ack from `mvm:defeat-choice` (or undefined if handler missing / void). */
export type MvMDefeatChoiceAck = {
  success?: boolean
  error?: string
  message?: string
}

interface MvMDefeatModalProps {
  mode: MvMDefeatModalMode
  /** Shown for standard defeat (reinforcing ally or losing attacker) */
  headline?: string
  /** Extra line under XP (e.g. defender ally: no resources lost) */
  standardDetail?: string
  characterXp?: number
  userXp?: number
  seizedResources: Record<string, number> | null
  /** Role-specific summary from mvm:battle-ended (server) */
  serverSummary?: string | null
  summaryOnly?: boolean
  onDefeatChoice?: (
    joinAlliance: boolean,
  ) => void | MvMDefeatChoiceAck | Promise<MvMDefeatChoiceAck | void>
  /** Castle auto-defense: show plunder summary only — no alliance incorporation choice (XP row controlled via `showBattleRewards`). */
  infoOnlyCastleOwnerDefeat?: boolean
  /** Dismiss label: defender/allies see "Back", attackers see "Back to Castle". */
  viewerBattleSide?: 'attacker' | 'defender' | null
  /** When set, overrides `viewerBattleSide`-based dismiss label (e.g. world-map auto battle → same as victory). */
  dismissButtonLabel?: string
  /**
   * When set, replaces the standard-defeat description block (`DefeatSummaryBlock` + headline + detail).
   */
  descriptionContent?: React.ReactNode
  /** When set, overrides XP row visibility (standard: default `!summaryOnly`; castle owner: default hide XP only for info-only auto-defense). */
  showBattleRewards?: boolean
  /**
   * Castle-owner defeat only: the attacker may still CONQUER (which returns your plundered
   * resources) or RAID. While the outcome is pending the "resources lost" section is hidden; it is
   * revealed only for a raid / declined conquer, and stays hidden if the castle is conquered.
   */
  awaitConquerOutcome?: boolean
  conquerOutcome?: 'raid' | 'conquered' | 'spared' | null
  onClose: () => void
  /** Raise when stacking above other fixed UI (e.g. global attack banner). */
  overlayZIndex?: number
}

function DefeatSummaryBlock({ serverSummary }: { serverSummary?: string | null }) {
  const lines = serverSummary?.trim()
    ? serverSummary
        .trim()
        .split(/\n+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3)
    : null
  if (!lines?.length) return null
  return (
    <div style={{ marginBottom: 'clamp(0.65dvh, 0.85dvh, 8px)' }}>
      {lines.map((line, i) => (
        <p
          key={i}
          style={{
            ...mvmResultSummaryTextStyle,
            marginTop: i ? 'clamp(0.45dvh, 0.65dvh, 6px)' : 0,
          }}
        >
          {line}
        </p>
      ))}
    </div>
  )
}

export const MvMDefeatModal: React.FC<MvMDefeatModalProps> = ({
  mode,
  headline,
  standardDetail,
  characterXp = 25,
  userXp = 25,
  seizedResources,
  serverSummary,
  summaryOnly = false,
  onDefeatChoice,
  infoOnlyCastleOwnerDefeat = false,
  viewerBattleSide = null,
  dismissButtonLabel: dismissButtonLabelProp,
  descriptionContent,
  showBattleRewards: showBattleRewardsProp,
  awaitConquerOutcome = false,
  conquerOutcome = null,
  onClose,
  overlayZIndex = MVM_RESULT_MODAL_OVERLAY_Z,
}) => {
  const { t } = useTranslation()
  const [choiceMade, setChoiceMade] = useState(false)
  const [choiceSubmitting, setChoiceSubmitting] = useState(false)

  // Hold the "resources lost" section until the attacker's conquer/raid outcome is known; if no
  // outcome arrives (attacker not in an alliance) reveal it after a short grace as a default raid.
  const [graceElapsed, setGraceElapsed] = useState(!awaitConquerOutcome)
  useEffect(() => {
    if (!awaitConquerOutcome) {
      setGraceElapsed(true)
      return
    }
    setGraceElapsed(false)
    const id = window.setTimeout(() => setGraceElapsed(true), 3500)
    return () => window.clearTimeout(id)
  }, [awaitConquerOutcome])
  const waitingForOutcome = awaitConquerOutcome && !conquerOutcome && !graceElapsed

  const backButtonLabel =
    dismissButtonLabelProp ??
    (viewerBattleSide === 'attacker'
      ? t('mvm.defeat.backToCastle')
      : t('mvm.defeat.back'))

  const handleChoice = async (join: boolean) => {
    if (choiceSubmitting) return
    setChoiceSubmitting(true)
    try {
      const res = (await onDefeatChoice?.(join)) as MvMDefeatChoiceAck | undefined
      if (res != null && res.success === false) {
        alert.open(
          res.error ||
            res.message ||
            t('mvm.defeat.choiceFailed', { defaultValue: 'Could not apply your choice.' }),
        )
        return
      }
      setChoiceMade(true)
    } catch {
      alert.open(t('mvm.defeat.choiceFailed', { defaultValue: 'Could not apply your choice.' }))
    } finally {
      setChoiceSubmitting(false)
    }
  }

  const loot =
    mode === 'castle_owner' && seizedResources && Object.keys(seizedResources).length > 0
      ? Object.entries(seizedResources).filter(([, v]) => (v as number) > 0)
      : []

  const lootRows = loot.map(([key, value]) => ({
    iconUrl: resourceIconForKey(key),
    name: t(`common.resources.${key}`, { defaultValue: key }),
    amount: value as number,
    sign: '−' as const,
  }))

  if (mode === 'standard') {
    const showXp = showBattleRewardsProp ?? !summaryOnly
    const description = descriptionContent ?? (
      <div>
        <DefeatSummaryBlock serverSummary={serverSummary} />
        {!summaryOnly ? (
          <p
            style={{
              ...mvmResultSummaryTextStyle,
              marginBottom: standardDetail ? 'clamp(0.45dvh, 0.65dvh, 6px)' : 0,
            }}
          >
            {headline || t('mvm.defeat.defaultHeadline')}
          </p>
        ) : null}
        {standardDetail ? <p style={mvmResultMutedStyle}>{standardDetail}</p> : null}
      </div>
    )

    return (
      <MvMResultModalShell
        variant="defeat"
        overlayZIndex={overlayZIndex}
        logoTitle={t('mvm.defeat.title')}
        description={description}
        battleRewardsTitle={t('mvm.resultModal.battleRewards')}
        showBattleRewards={showXp}
        characterXp={characterXp}
        userXp={userXp}
        characterXpLabel={t('mvm.resultModal.characterXp')}
        userXpLabel={t('mvm.resultModal.accountXp')}
        lootSectionTitle={t('mvm.defeat.resourcesLost')}
        showLootSection={false}
        lootRows={[]}
        backButtonLabel={backButtonLabel}
        onClose={onClose}
      />
    )
  }

  const showXpCastleOwner = showBattleRewardsProp ?? !infoOnlyCastleOwnerDefeat

  const castleDescription = (
    <div>
      <DefeatSummaryBlock serverSummary={serverSummary} />
      <p style={{ ...mvmResultSummaryTextStyle, marginBottom: 'clamp(0.45dvh, 0.65dvh, 6px)' }}>
        {infoOnlyCastleOwnerDefeat ? t('mvm.defeat.autoDefenseConquered') : t('mvm.defeat.castleConquered')}
      </p>
      {infoOnlyCastleOwnerDefeat && !showXpCastleOwner ? (
        <p style={mvmResultMutedStyle}>{t('mvm.defeat.noXpAutoDefense')}</p>
      ) : (
        <p style={mvmResultMutedStyle}>
          {t('mvm.defeat.defendersXpHint', {
            defaultValue: 'Defenders still earn experience from the battle.',
          })}
        </p>
      )}
    </div>
  )

  const choiceBlock =
    !infoOnlyCastleOwnerDefeat && !choiceMade ? (
      <>
        <p
          style={{
            color: TEXT_PRIMARY,
            fontSize: MVM_RESULT_MODAL_RV.sectionTitleFont,
            margin: `0 0 clamp(0.85dvh, 1.1dvh, 10px)`,
            fontWeight: 700,
          }}
        >
          {t('mvm.defeat.choicePrompt')}
        </p>
        <ul
          style={{
            color: mvmResultMutedStyle.color,
            fontSize: 'clamp(1.15dvh, 1.5dvh, 12px)',
            margin: `0 0 clamp(0.95dvh, 1.35dvh, 14px)`,
            paddingLeft: 'clamp(14px, 2.2dvh, 20px)',
            textAlign: 'left',
            lineHeight: 1.55,
            fontFamily: 'system-ui, sans-serif',
            fontWeight: 500,
          }}
        >
          <li style={{ marginBottom: 'clamp(0.55dvh, 0.85dvh, 8px)' }}>
            <span style={{ color: TEXT_PRIMARY }}>{t('mvm.defeat.incorporateOptionDesc')}</span>
          </li>
          <li>
            {t('mvm.defeat.stayOptionDesc')}{' '}
            <span style={{ color: '#e8a0a0', fontWeight: 700 }}>{t('mvm.defeat.stayOptionLoss')}</span>{' '}
            {t('mvm.defeat.stayOptionSuffix')}
          </li>
        </ul>
        <div
          style={{
            display: 'flex',
            gap: MVM_RESULT_MODAL_RV.battleRewardsGap,
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: 'clamp(0.35dvh, 0.45dvh, 4px)',
          }}
        >
          <button
            type="button"
            disabled={choiceSubmitting}
            onClick={() => void handleChoice(true)}
            style={{
              padding: MVM_RESULT_MODAL_RV.backBtnPad,
              borderRadius: 'clamp(0.55dvh, 0.85dvh, 8px)',
              border: 'none',
              background: 'rgba(74,144,217,.85)',
              color: 'white',
              fontSize: MVM_RESULT_MODAL_RV.backBtnFont,
              fontWeight: 700,
              fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
              cursor: choiceSubmitting ? 'wait' : 'pointer',
              opacity: choiceSubmitting ? 0.7 : 1,
              flex: '1 1 min(36vw, 22dvh, 160px)',
            }}
          >
            {choiceSubmitting ? t('common.loading') : t('mvm.defeat.incorporateButton')}
          </button>
          <button
            type="button"
            disabled={choiceSubmitting}
            onClick={() => void handleChoice(false)}
            style={{
              padding: MVM_RESULT_MODAL_RV.backBtnPad,
              borderRadius: 'clamp(0.55dvh, 0.85dvh, 8px)',
              border: `1px solid ${BORDER}`,
              background: 'rgba(217,74,74,.18)',
              color: '#e8a0a0',
              fontSize: MVM_RESULT_MODAL_RV.backBtnFont,
              fontWeight: 700,
              fontFamily: MVM_BATTLE_UI_FONT_FAMILY,
              cursor: choiceSubmitting ? 'wait' : 'pointer',
              opacity: choiceSubmitting ? 0.7 : 1,
              flex: '1 1 min(36vw, 22dvh, 160px)',
            }}
          >
            {choiceSubmitting ? t('common.loading') : t('mvm.defeat.stayButton')}
          </button>
        </div>
      </>
    ) : null

  const confirmedLine =
    !infoOnlyCastleOwnerDefeat && choiceMade ? (
      <p style={{ ...mvmResultMutedStyle, marginBottom: 'clamp(0.85dvh, 1.35dvh, 12px)' }}>
        {t('mvm.defeat.choiceConfirmed')}
      </p>
    ) : null

  // Only show what was plundered for a raid / declined conquer — never while pending, if conquered,
  // or if the attempted recruitment fell through (the resources went back either way).
  const showLootBlock =
    lootRows.length > 0 && !waitingForOutcome && conquerOutcome !== 'conquered' && conquerOutcome !== 'spared'

  const castleDescriptionResolved =
    conquerOutcome === 'spared' ? (
      <div>
        <DefeatSummaryBlock serverSummary={serverSummary} />
        <p style={{ ...mvmResultSummaryTextStyle, marginBottom: 'clamp(0.45dvh, 0.65dvh, 6px)' }}>
          {t('mvm.defeat.castleFell', { defaultValue: 'Your castle fell.' })}
        </p>
        <p style={mvmResultMutedStyle}>
          {t('mvm.defeat.spared', {
            defaultValue: 'The victors tried to recruit you but had no room — your resources were returned.',
          })}
        </p>
      </div>
    ) : conquerOutcome === 'conquered' ? (
      <div>
        <DefeatSummaryBlock serverSummary={serverSummary} />
        <p style={{ ...mvmResultSummaryTextStyle, marginBottom: 'clamp(0.45dvh, 0.65dvh, 6px)' }}>
          {t('mvm.defeat.castleConquered', { defaultValue: 'Your castle was conquered.' })}
        </p>
        <p style={mvmResultMutedStyle}>
          {t('mvm.defeat.conqueredResourcesReturned', {
            defaultValue: 'You were absorbed into the victors’ alliance — your resources were returned.',
          })}
        </p>
      </div>
    ) : waitingForOutcome ? (
      <div>
        <DefeatSummaryBlock serverSummary={serverSummary} />
        <p style={{ ...mvmResultSummaryTextStyle, marginBottom: 'clamp(0.45dvh, 0.65dvh, 6px)' }}>
          {t('mvm.defeat.castleFell', { defaultValue: 'Your castle fell.' })}
        </p>
        <p style={mvmResultMutedStyle}>
          {t('mvm.defeat.outcomePending', { defaultValue: 'The victor is deciding whether to conquer or raid…' })}
        </p>
      </div>
    ) : (
      castleDescription
    )

  return (
    <MvMResultModalShell
      variant="defeat"
      overlayZIndex={overlayZIndex}
      logoTitle={t('mvm.defeat.title')}
      description={castleDescriptionResolved}
      battleRewardsTitle={t('mvm.resultModal.battleRewards')}
      showBattleRewards={showXpCastleOwner}
      characterXp={characterXp}
      userXp={userXp}
      characterXpLabel={t('mvm.resultModal.characterXp')}
      userXpLabel={t('mvm.resultModal.accountXp')}
      lootSectionTitle={t('mvm.defeat.resourcesLost')}
      showLootSection={showLootBlock}
      lootRows={lootRows}
      backButtonLabel={backButtonLabel}
      onClose={onClose}
      betweenLootAndBack={
        <>
          {choiceBlock}
          {confirmedLine}
        </>
      }
      suppressBackButton={!infoOnlyCastleOwnerDefeat && !choiceMade}
    />
  )
}
